"use client";

/**
 * Root Error Boundary
 * 
 * Purpose: Catches JavaScript errors in the component tree and displays a fallback UI.
 * Prevents the entire page from crashing due to errors in child components.
 * 
 * Usage:
 * ```tsx
 * <RootErrorBoundary>
 *   <YourComponent />
 * </RootErrorBoundary>
 * ```
 * 
 * Note: This is a Client Component because React Error Boundaries require
 * class components with componentDidCatch lifecycle method.
 * 
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import Link from "next/link";

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (process.env.NODE_ENV !== "production") {
      console.error("[RootErrorBoundary] Caught error:", error);
      console.error("[RootErrorBoundary] Component stack:", errorInfo.componentStack);
    }
    
    // TODO: Send error to your error tracking service (e.g., Sentry, LogRocket)
    // Example:
    // captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI provided via props
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "2rem",
            fontFamily: "system-ui, -apple-system, sans-serif",
            backgroundColor: "#f9fafb",
            color: "#111827",
          }}
        >
          <div
            style={{
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <h1
              style={{
                fontSize: "1.5rem",
                fontWeight: 600,
                marginBottom: "1rem",
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                color: "#6b7280",
                marginBottom: "1.5rem",
                lineHeight: 1.5,
              }}
            >
              We&apos;re sorry, but something unexpected happened. Please try again or contact support if the problem persists.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#111827",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "0.375rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Try Again
              </button>
              <Link
                href="/"
                style={{
                  padding: "0.5rem 1rem",
                  backgroundColor: "#ffffff",
                  color: "#111827",
                  border: "1px solid #d1d5db",
                  borderRadius: "0.375rem",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Go Home
              </Link>
            </div>
            {process.env.NODE_ENV !== "production" && this.state.error && (
              <details
                style={{
                  marginTop: "1.5rem",
                  textAlign: "left",
                  backgroundColor: "#fef2f2",
                  padding: "1rem",
                  borderRadius: "0.375rem",
                  border: "1px solid #fecaca",
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    fontWeight: 500,
                    color: "#991b1b",
                  }}
                >
                  Error Details (Dev Only)
                </summary>
                <pre
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.75rem",
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    color: "#7f1d1d",
                  }}
                >
                  {this.state.error.toString()}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RootErrorBoundary;

